import React, { useState } from 'react'
import { Box, Paper, Typography } from '@mui/material'
import { CommonTextField, CommonButton, CommonPhone } from '@/components/common'
import { useTenantContext } from '@/context'
import { useChairpersonRequestMagicLink } from '@/hooks'

const STATUS = {
  IDLE: 'IDLE',
  NOT_FOUND: 'CHAIRPERSON_NOT_FOUND',
  SENT: 'MAGIC_LINK_SENT',
}

export const ChairpersonAccountRecovery = ({ onSuccess, onBack }) => {
  const { tenant } = useTenantContext()
  const { loadingRequestMagicLink, requestMagicLink } =
    useChairpersonRequestMagicLink()

  const [status, setStatus] = useState(STATUS.IDLE)
  const [errors, setErrors] = useState({})
  const [formData, setFormData] = useState({
    email: '',
    phoneNumber: '',
  })
  const [isUnknownError, setIsUnknownError] = useState(false)

  const handleAccountRecovery = async e => {
    if (e) e.preventDefault()
    setErrors({})
    setIsUnknownError(false)

    try {
      const response = await requestMagicLink(formData)
      const responseCode = getResponseCode(response?.response)
      if (responseCode === 'MAGIC_LINK_SENT') {
        setStatus(STATUS.SENT)
        resetForm()
      }
    } catch (error) {
      if (error?.response?.status === 400) {
        const raw = error?.response?.data?.response
        const responseCode = getResponseCode(raw)
        if (responseCode === 'CHAIRPERSON_NOT_FOUND') {
          setStatus(STATUS.NOT_FOUND)
        } else {
          setIsUnknownError(true)
        }
      } else {
        setIsUnknownError(true)
      }
    }
  }

  const getResponseCode = raw => {
    if (typeof raw === 'string') {
      const [codeKey] = raw.split(':', 2)
      return codeKey
    }
    return null
  }

  const resetForm = () => {
    setFormData({
      email: '',
      phoneNumber: '',
    })
    setErrors({})
    setIsUnknownError(false)
  }

  const getHeaderText = () => {
    switch (status) {
      case STATUS.NOT_FOUND:
        return {
          title: 'Account Not Found',
          subtitle:
            'We did not find a chairperson account with that contact information. Enter another email or phone to try again.',
        }
      case STATUS.SENT:
        return {
          title: 'Magic Link is on the way!',
          subtitle: 'Your magic link has been sent to your contact information',
        }
      default:
        return {
          title: 'Account Recovery',
          subtitle:
            "Enter your contact information, and we'll send a link to login",
        }
    }
  }

  const { title, subtitle } = getHeaderText()

  return (
    <Paper
      sx={{
        p: { xs: 3, md: 5 },
        width: '100%',
        maxWidth: 540,
        borderRadius: 2,
      }}
    >
      <Box
        component='form'
        onSubmit={handleAccountRecovery}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
        }}
      >
        <Typography
          variant='h4'
          fontWeight={500}
          sx={{
            textAlign: 'center',
            mb: 2,
          }}
        >
          {title}
          <br />
          <Typography
            variant='body1'
            fontWeight={500}
            sx={{
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            {subtitle}
          </Typography>
        </Typography>

        {isUnknownError && (
          <Typography
            variant='h6'
            fontWeight={500}
            sx={{ textAlign: 'center', my: 2 }}
          >
            An unknown error occurred, please try again later.
            <br />
            If you have questions please call{' '}
            <a href={`tel:${tenant?.tenant_info?.phone_number}`}>
              {tenant?.tenant_info?.phone_number}
            </a>
          </Typography>
        )}

        <CommonTextField
          id='email'
          label='Email'
          value={formData.email}
          setValue={value => setFormData({ ...formData, email: value })}
          type='email'
          error={errors.email}
          disabled={!!formData.phoneNumber}
          placeholder='Enter your email'
          inputProps={{
            name: 'email',
            autoComplete: 'username email',
          }}
        />

        {tenant?.configuration_options?.find(
          item => item.code === 'USE_MAGIC_LINK_SMS'
        )?.value === 'true' && (
          <CommonPhone
            id='phone-number'
            label='Phone Number'
            value={formData.phoneNumber}
            setValue={value => setFormData({ ...formData, phoneNumber: value })}
            mask='(999) 999-9999'
            error={errors.phoneNumber}
            disabled={!!formData.email}
            placeholder='(###) ###-####'
            inputProps={{
              name: 'phoneNumber',
            }}
          />
        )}

        <CommonButton
          id='send-link-button'
          label={status === STATUS.SENT ? 'Resend Link' : 'Send Link'}
          type='submit'
          onClick={handleAccountRecovery}
          loading={loadingRequestMagicLink}
          disabled={!formData.email && !formData.phoneNumber}
          size='large'
          style={{ width: '100%' }}
        />

        <CommonButton
          id='back-to-login-button'
          label='Back to Login'
          link
          variant='text'
          onClick={onBack}
          size='small'
          sx={{ fontWeight: 400, mt: 2 }}
        />
      </Box>
    </Paper>
  )
}
